import React, { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslate } from 'react-admin';
import EmptyTableToolbar from '../table/EmptyTableToolbar';
import { formatCurrency } from '../../utils/formatting';

const money = (value) => formatCurrency(Number(value) || 0);

const SellersDetailTable = ({ sellers = [] }) => {
  const translate = useTranslate();
  if (!sellers.length) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 1, px: 0.5 }}>
        {translate('report.no_seller_records', { _: 'Bu kalemde satıcı kaydı yok.' })}
      </Typography>
    );
  }

  return (
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f0f0f0' }}>
          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{translate('report.seller', { _: 'Satıcı' })}</TableCell>
          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{translate('report.user', { _: 'Kullanıcı' })}</TableCell>
          <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>{translate('report.contact', { _: 'İletişim' })}</TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>
            {translate('report.count', { _: 'Adet' })}
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12 }}>
            {translate('report.amount', { _: 'Tutar' })}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sellers.map((seller) => (
          <TableRow key={seller.id} hover>
            <TableCell sx={{ fontSize: 13 }}>{seller.name || seller.id}</TableCell>
            <TableCell sx={{ fontSize: 13 }}>
              {seller.userName || seller.userId || '—'}
            </TableCell>
            <TableCell sx={{ fontSize: 12 }}>
              {[seller.userEmail, seller.userMobile].filter(Boolean).join(' · ') || '—'}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {seller.count || 0}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: 13 }}>
              {money(seller.amount)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

/** Ownership kalemleri: her kalem açılır accordion; içinde affiliate/promoter user listesi */
export const OwnershipLinesTable = ({ lines = [], totalLabel, total = 0 }) => {
  const translate = useTranslate();
  const label = totalLabel || translate('report.total', { _: 'Toplam' });

  const getLineLabel = (line) => {
    if (line.key === 'affiliate') {
      return line.label?.includes('bağlı değil')
        ? translate('report.line_affiliate_unbound', { _: 'Affiliate (bağlı değil)' })
        : translate('report.line_affiliate', { _: 'Affiliate satışları' });
    }
    if (line.key === 'salePoint') {
      return line.label?.includes('bağlı değil')
        ? translate('report.line_sale_point_unbound', { _: 'Sales Point (bağlı değil)' })
        : translate('report.line_sale_point', { _: 'Sales Point satışları' });
    }
    if (line.key === 'promoter') {
      return translate('report.line_promoter', { _: 'Promoter satışları' });
    }
    if (line.key === 'managerCash') {
      return translate('report.line_manager_cash', { _: 'Manager nakit' });
    }
    if (line.key === 'website') {
      return translate('report.line_website', { _: 'Website' });
    }
    if (line.key === 'mobile') {
      return translate('report.line_mobile', { _: 'Mobil' });
    }
    if (line.key === 'saleAgent') {
      return translate('report.line_sale_agent', { _: 'Satış Temsilcisi' });
    }
    if (line.key === 'other') {
      return translate('report.line_other', { _: 'Diğer' });
    }
    return translate(`report.line_${line.key}`, { _: line.label || line.key });
  };

  return (
    <Box sx={{ mt: 0.5 }}>
      {lines.map((line) => (
        <Accordion
          key={line.key}
          disableGutters
          elevation={0}
          sx={{
            border: '1px solid #eee',
            borderRadius: '6px !important',
            mb: 1,
            '&:before': { display: 'none' },
            overflow: 'hidden',
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{
              minHeight: 44,
              px: 1.5,
              backgroundColor: '#fafafa',
              '& .MuiAccordionSummary-content': {
                my: 0.75,
                alignItems: 'center',
                justifyContent: 'space-between',
                pr: 1,
                gap: 2,
              },
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {getLineLabel(line)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', whiteSpace: 'nowrap' }}>
              <Typography variant="caption" color="text.secondary">
                {line.count || 0} {translate('report.sales_count_suffix', { _: 'satış' })}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {money(line.amount)}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 1, pb: 1.5, pt: 0 }}>
            <SellersDetailTable sellers={line.sellers || []} />
          </AccordionDetails>
        </Accordion>
      ))}

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          mt: 0.5,
          borderTop: '1px solid #eee',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {lines.reduce((sum, l) => sum + (l.count || 0), 0)} {translate('report.sales_count_suffix', { _: 'satış' })}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            {money(total)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Sale Report section: kapalıyken başlık + toplam, açıkken DataGrid veya custom details.
 */
const SaleReportAccordionSection = ({
  title,
  icon,
  totalLabel,
  totalValue,
  formatTotalAsCurrency = true,
  rows = [],
  columns = [],
  details = null,
  defaultExpanded = false,
}) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(!!defaultExpanded);
  const resolvedTotalLabel = totalLabel || translate('report.total', { _: 'Toplam' });

  const displayTotal =
    totalValue == null || totalValue === ''
      ? '—'
      : formatTotalAsCurrency
        ? formatCurrency(Number(totalValue) || 0)
        : String(totalValue);

  const translatedColumns = columns.map((c) => ({
    ...c,
    flex: 1,
    minWidth: c.width || 120,
    headerName: translate(`report.col_${c.field}`, { _: c.headerName || c.field }),
  }));

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid #e6e6e6',
        borderRadius: '8px !important',
        mb: 2,
        '&:before': { display: 'none' },
        overflow: 'hidden',
        width: '100%',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 52,
          px: 2,
          backgroundColor: expanded ? '#fafafa' : '#fff',
          '& .MuiAccordionSummary-content': {
            my: 1,
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: 1,
            gap: 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
          {icon}
          <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
          <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
            {resolvedTotalLabel}
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 700 }}>
            {displayTotal}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 1.5, pb: 2, pt: 0 }}>
        {details != null ? (
          details
        ) : (
          <DataGrid
            rows={rows}
            density="compact"
            autoHeight
            disableRowSelectionOnClick
            initialState={{
              columns: { columnVisibilityModel: {} },
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            style={{ width: '100%', fontSize: 12 }}
            getRowId={(row) => row.id}
            columns={translatedColumns}
            hideFooter
            slots={{ toolbar: EmptyTableToolbar }}
            sx={{
              border: 0,
              '.MuiDataGrid-columnHeaders': {
                backgroundColor: '#f5f5f5',
                borderBottom: '1px solid #eee',
              },
              '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#fcfcfc' },
            }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default SaleReportAccordionSection;
